package com.arena_web.ui.sections.arena

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.EventDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import kotlin.js.Date

enum class EventStatus {
    NEW, IN_PROGRESS, COMPLETED, CANCELED
}

data class ArenaEvent(
    val id: String = "",
    val title: String = "",
    val date: String = "",
    val time: String = "",
    val team1: String? = null,
    val team2: String? = null,
    val location: String = "",
    val city: String? = null,
    val status: EventStatus = EventStatus.NEW,
    val imageUrl: String = "",
    val teamsCount: Int? = null,
    val membersJoined: Int? = null,
    val membersTotal: Int? = null,
    val format: String? = null,
    val cost: String? = null,
    val isLive: Boolean = false,
    val finalScore: String? = null,
    val duration: String? = null,
    val communityId: String? = null,
    val rawDate: String = "",
    val latitude: String = "",
    val longitude: String = "",
    /** Canonical sport bucket — drives icon selection & palette. */
    val sportName: String = "",
    /** Display label: user-typed name on "Other" events, sport name otherwise. */
    val sportLabel: String = "",
)

fun EventDto.toArenaEvent(): ArenaEvent {
    val parsed = Date(dateTime)
    val months = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
    val dateStr = "${parsed.getDate()} ${months[parsed.getMonth()]}"
    val rawDateStr = "${parsed.getFullYear()}-${(parsed.getMonth() + 1).toString().padStart(2, '0')}-${parsed.getDate().toString().padStart(2, '0')}"
    val timeStr = "${parsed.getHours().toString().padStart(2, '0')}:${parsed.getMinutes().toString().padStart(2, '0')}"

    val eventStatus = when (status.uppercase()) {
        "IN_PROGRESS" -> EventStatus.IN_PROGRESS
        "COMPLETED", "FINISHED" -> EventStatus.COMPLETED
        "CANCELED", "CANCELLED" -> EventStatus.CANCELED
        else -> EventStatus.NEW
    }

    val team1Name = teamScores.getOrNull(0)?.name
    val team2Name = teamScores.getOrNull(1)?.name

    val score = if (eventStatus == EventStatus.COMPLETED && teamScores.size >= 2) {
        "${teamScores[0].score} – ${teamScores[1].score}"
    } else null

    val formatStr = if (format != null) "${format} X ${format}" else null
    val costStr = if (cost != "0" && cost != "0.00") "$cost €" else "Free"

    // Duration is computed from the full event timer on the detail screen;
    // the list DTO doesn't carry it any more.
    val durationStr: String? = null

    // Display label: prefer the user-typed customSportName when the event was
    // created under "Other" (backend ships sportName="Other" + customSportName).
    // For regular sports just use the canonical name.
    val sportDisplayLabel = customSportName?.takeIf { it.isNotBlank() } ?: sportName

    return ArenaEvent(
        id = id,
        title = title,
        date = dateStr,
        time = timeStr,
        team1 = team1Name,
        team2 = team2Name,
        location = address,
        city = city,
        status = eventStatus,
        imageUrl = imageUrl ?: "/icons/event_football_field.png",
        teamsCount = teamsCount,
        membersJoined = participantsCount,
        membersTotal = maxPlayers,
        format = formatStr,
        cost = costStr,
        isLive = eventStatus == EventStatus.IN_PROGRESS,
        finalScore = score,
        duration = durationStr,
        communityId = chatId,
        rawDate = rawDateStr,
        latitude = latitude,
        longitude = longitude,
        sportName = sportName,
        sportLabel = sportDisplayLabel,
    )
}

@Composable
fun ArenaEventCard(
    event: ArenaEvent,
    modifier: Modifier = Modifier,
    onClick: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val isCompleted = event.status == EventStatus.COMPLETED || event.status == EventStatus.CANCELED
    val textColor = if (isCompleted) org.jetbrains.compose.web.css.Color("#959697") else org.jetbrains.compose.web.css.Color("#BED6F5") // theme-invariant: over photo scrim
    val headingColor = if (isCompleted) AppColors.Text.Muted else AppColors.PrimaryWhite
    val subtitleColor = org.jetbrains.compose.web.css.Color("#959697") // theme-invariant: over photo scrim

    Box(
        modifier = modifier
            .overflow(Overflow.Hidden)
            .sBorderRadius(16)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property("background",
                    "url('${event.imageUrl}') center/cover no-repeat padding-box, " +
                    "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
            }
    ) {
        // Overlay
        if (isCompleted) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .backgroundColor(Color.rgba(2, 4, 11, 0.85f))
            )
        } else {
            Box(
                modifier = Modifier
                    .sWidth(396)
                    .fillMaxHeight()
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .styleModifier {
                        // theme-invariant: photo card keeps the dark scrim in both themes
                        property(
                            "background-image",
                            "linear-gradient(90deg, rgba(0, 0, 0, 0.80) 10%, rgba(0, 0, 0, 0.60) 30%, " +
                                "rgba(0, 0, 0, 0.40) 50%, rgba(0, 0, 0, 0.20) 70%, rgba(153, 153, 153, 0) 91.4%)"
                        )
                    }
            )
        }

        // Right column: status badge + action button
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .position(Position.Absolute)
                .top(0.px).right(0.px)
                .sPadding(22),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.End
        ) {
            EventStatusBadge(event.status, sportColors)
            // CTA copy depends on event status — joining isn't possible once
            // the match goes Live or has wrapped up. Canceled events have no
            // CTA at all.
            val actionLabel = when (event.status) {
                EventStatus.COMPLETED -> t.arenaActionViewResults
                EventStatus.IN_PROGRESS -> t.arenaActionWatchLive
                EventStatus.NEW -> t.arenaActionJoinNow
                EventStatus.CANCELED -> null
            }
            if (actionLabel != null) {
                EventActionButton(text = actionLabel, sportColors = sportColors)
            }
        }

        // Left column: event information
        Column(
            modifier = Modifier
                .sMaxWidth(352)
                .fillMaxHeight()
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .sPaddingLeft(18)
                .sPaddingTop(100)
                .sPaddingBottom(22),
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            // Top: headline + teams + location
            Column(modifier = Modifier.sGap(28)) {
                // Date & Time
                Row(
                    modifier = Modifier.sGap(12),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        "${event.date} ",
                        modifier = Modifier.h1().color(headingColor)
                    )
                    Box(
                        modifier = Modifier
                            .sSize(8)
                            .borderRadius(50.percent)
                            .backgroundColor(headingColor)
                    )
                    SpanText(
                        event.time,
                        modifier = Modifier.h1().color(headingColor)
                    )
                }

                // Headline — always the raw event title typed at creation,
                // with the location underneath.
                Column(modifier = Modifier.sGap(12)) {
                    if (event.title.isNotBlank()) {
                        SpanText(
                            event.title,
                            modifier = Modifier.h2().color(textColor).styleModifier {
                                property("display", "-webkit-box")
                                property("-webkit-line-clamp", "2")
                                property("-webkit-box-orient", "vertical")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                            }
                        )
                    }
                    SpanText(
                        event.location,
                        modifier = Modifier.body2xlRegular().color(subtitleColor)
                    )
                }
            }

            // Bottom: meta info — Figma 8438:274731. Teams / Members / Format /
            // Cost are shown for every status; missing fields collapse away.
            FullEventMeta(event, textColor)
        }
    }
}

@Composable
private fun EventStatusBadge(status: EventStatus, sportColors: SportColorScheme) {
    val t = LocalAppStrings.current
    // Live state is rendered as a non-button `● Live` indicator; everything
    // else keeps the pill style.
    if (status == EventStatus.IN_PROGRESS) {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .sSize(8)
                    .borderRadius(50.percent)
                    .backgroundColor(Color.rgb(242, 85, 85))
            )
            SpanText(
                t.arenaStatusLive,
                modifier = Modifier
                    .bodyLMedium()
                    .color(AppColors.PrimaryWhite)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
        return
    }

    val bgColor: Color
    val textColor: Color
    val label: String

    when (status) {
        EventStatus.NEW -> {
            bgColor = Color.rgb(0, 97, 255) // Blue
            textColor = Color.rgb(255, 255, 255)
            label = t.arenaStatusNew
        }
        EventStatus.COMPLETED -> {
            bgColor = Color.rgb(52, 213, 36) // Green
            textColor = Color.rgb(0, 0, 0)
            label = t.arenaStatusCompleted
        }
        EventStatus.CANCELED -> {
            bgColor = Color.rgb(242, 85, 85) // Red
            textColor = Color.rgb(255, 255, 255)
            label = t.arenaStatusCanceled
        }
        EventStatus.IN_PROGRESS -> return // handled above
    }

    Box(
        modifier = Modifier
            .backgroundColor(bgColor)
            .sBorderRadius(22)
            .sPadding(8, 22),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier
                .bodyLMedium()
                .color(textColor)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

@Composable
private fun EventActionButton(
    text: String,
    sportColors: SportColorScheme,
    onClick: () -> Unit = {}
) {
    Row(
        modifier = Modifier
            .backgroundColor(sportColors.primary)
            .sBorderRadius(22)
            .sHeight(44)
            .sPaddingLeft(36)
            .sPaddingRight(4)
            .sPaddingTop(4)
            .sPaddingBottom(4)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .sGap(10),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            text,
            modifier = Modifier
                .bodyLMedium()
                .color(sportColors.onPrimary)
                .styleModifier { property("white-space", "nowrap") }
        )
        Box(
            modifier = Modifier
                .sSize(36)
                .sBorderRadius(100)
                .overflow(Overflow.Hidden)
                .backgroundColor(sportColors.secondary),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.ArrowUpOutline,
                modifier = Modifier.styleModifier {
                    property("transform", "rotate(45deg)")
                    property("filter", sportColors.iconFilterOnSecondary)
                }
            )
        }
    }
}

@Composable
private fun MetaRow(
    iconSrc: String,
    content: @Composable RowScope.() -> Unit
) {
    Row(
        modifier = Modifier.sGap(8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = iconSrc)
        content()
    }
}

@Composable
private fun FullEventMeta(event: ArenaEvent, textColor: org.jetbrains.compose.web.css.CSSColorValue) {
    val t = LocalAppStrings.current
    // Members fallback to total-only when joined isn't supplied, and vice versa.
    val membersText = when {
        event.membersJoined != null && event.membersTotal != null ->
            t.arenaMembersJoinedTotal(event.membersJoined, event.membersTotal)
        event.membersJoined != null -> t.arenaMembersCount(event.membersJoined)
        event.membersTotal != null -> t.arenaMembersCount(event.membersTotal)
        else -> null
    }

    // Collect only the chips we have data for, then lay them out in a wrapping
    // 2-column flex so missing fields don't leave empty slots behind.
    val chips = buildList<@Composable () -> Unit> {
        // Sport chip — uses customSportName for "Other" events (e.g.
        // "Skateboard") so the canonical bucket name isn't shown to users.
        if (event.sportLabel.isNotBlank()) add {
            MetaRow(AppIcons.ballSmall(event.sportName)) {
                SpanText(event.sportLabel, modifier = Modifier.bodyLRegular().color(textColor))
            }
        }
        if (event.teamsCount != null && event.teamsCount > 0) add {
            MetaRow(AppIcons.Shield) {
                SpanText(t.arenaTeamsCount(event.teamsCount), modifier = Modifier.bodyLRegular().color(textColor))
            }
        }
        if (membersText != null) add {
            MetaRow(AppIcons.People) {
                SpanText(membersText, modifier = Modifier.bodyLRegular().color(textColor))
            }
        }
        if (event.format != null) add {
            MetaRow(AppIcons.FootballField) {
                SpanText(t.arenaFormat(event.format), modifier = Modifier.bodyLRegular().color(textColor))
            }
        }
        if (event.cost != null) add {
            MetaRow(AppIcons.Wallet) {
                SpanText(event.cost, modifier = Modifier.bodyLRegular().color(textColor))
            }
        }
    }
    if (chips.isEmpty()) return

    Box(
        modifier = Modifier.styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("gap", "${s(8)} ${s(28)}")
        }
    ) {
        chips.forEach { it() }
    }
}

@Composable
private fun LiveEventMeta(event: ArenaEvent, textColor: Color) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.sGap(10)) {
        if (event.isLive) {
            Row(
                modifier = Modifier.sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .sSize(8)
                        .borderRadius(50.percent)
                        .backgroundColor(Color.rgb(242, 85, 85))
                )
                SpanText(
                    t.arenaStatusLive,
                    modifier = Modifier.bodyLRegular().color(textColor)
                )
            }
        }
        if (event.membersJoined != null && event.membersTotal != null) {
            MetaRow(AppIcons.People) {
                SpanText(
                    t.arenaMembersJoinedTotal(event.membersJoined, event.membersTotal),
                    modifier = Modifier.bodyLRegular().color(textColor)
                )
            }
        }
    }
}

@Composable
private fun CompletedEventMeta(event: ArenaEvent) {
    val t = LocalAppStrings.current
    val textColor = AppColors.Text.Muted
    Column(modifier = Modifier.sGap(8)) {
        if (event.finalScore != null) {
            MetaRow(AppIcons.Trophy) {
                SpanText(
                    t.arenaFinalScore(event.finalScore),
                    modifier = Modifier.bodyLRegular().color(textColor)
                )
            }
        }
        if (event.membersTotal != null) {
            MetaRow(AppIcons.People) {
                SpanText(
                    t.arenaMembersCount(event.membersTotal),
                    modifier = Modifier.bodyLRegular().color(textColor)
                )
            }
        }
        if (event.duration != null) {
            MetaRow(AppIcons.Time) {
                SpanText(
                    t.arenaDuration(event.duration),
                    modifier = Modifier.bodyLRegular().color(textColor)
                )
            }
        }
    }
}
